"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PullToRefresh = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
/**
 * Pull down from the top of the page to reload its data (touch screens only). Render it once in a
 * page: <PullToRefresh onRefresh={loadAll} />. It listens on the window, so it doesn't wrap or
 * change the page's layout — it only shows a small indicator at the top while pulling.
 */
const TRIGGER = 70;
const MAX = 110;
function PullToRefresh({ onRefresh }) {
    const [pull, setPull] = (0, react_1.useState)(0);
    const [refreshing, setRefreshing] = (0, react_1.useState)(false);
    const startY = (0, react_1.useRef)(null);
    const pullRef = (0, react_1.useRef)(0);
    const refreshRef = (0, react_1.useRef)(onRefresh);
    refreshRef.current = onRefresh;
    (0, react_1.useEffect)(() => {
        const onStart = (e) => {
            // Only from the very top of the page, and not while a modal/drawer is scrolling.
            startY.current = window.scrollY <= 0 && e.touches.length === 1 ? e.touches[0].clientY : null;
        };
        const onMove = (e) => {
            if (startY.current === null)
                return;
            const dy = e.touches[0].clientY - startY.current;
            if (dy <= 0 || window.scrollY > 0) {
                pullRef.current = 0;
                setPull(0);
                return;
            }
            pullRef.current = Math.min(MAX, dy * 0.5);
            setPull(pullRef.current);
        };
        const onEnd = async () => {
            const reached = pullRef.current >= TRIGGER;
            startY.current = null;
            pullRef.current = 0;
            setPull(0);
            if (!reached)
                return;
            setRefreshing(true);
            try {
                await refreshRef.current();
            }
            finally {
                setRefreshing(false);
            }
        };
        window.addEventListener('touchstart', onStart, { passive: true });
        window.addEventListener('touchmove', onMove, { passive: true });
        window.addEventListener('touchend', onEnd);
        return () => {
            window.removeEventListener('touchstart', onStart);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onEnd);
        };
    }, []);
    if (!refreshing && pull === 0)
        return null;
    const ready = pull >= TRIGGER;
    return ((0, jsx_runtime_1.jsx)("div", { "aria-live": "polite", style: {
            position: 'fixed',
            top: refreshing ? 72 : 16 + pull * 0.6,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2500,
            background: '#fff',
            color: '#333',
            borderRadius: 999,
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 600,
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            pointerEvents: 'none',
            opacity: refreshing ? 1 : Math.min(1, pull / TRIGGER),
        }, children: refreshing ? '↻ Refreshing…' : ready ? '↑ Release to refresh' : '↓ Pull to refresh' }));
}
exports.PullToRefresh = PullToRefresh;
exports.default = PullToRefresh;
